<template>
    <section class="page-item">
        <div class="row">
            <div class="col grid-margin stretch-card">
                <div class="card">
                    <div class="card-body">
                        <h4 v-if="$attrs.mode === 'new'">{{ $t('createNewTitle') }}</h4>
                        <h4 v-else class="card-title">{{ $t('editEntry') }} <strong>{{pageName}}</strong> <code>(id {{page.id}})</code></h4>
                        <p class="card-description pb-4">{{ $t('fillFieldsBelow') }}</p>
                        <div class="forms-sample">
                            <b-form-group :label="$t('forms.name')" label-for="name-input" class="mb-4">
                                <div class="row align-items-center">
                                    <div class="col-auto text-right min-45px-w">{{ lang.toUpperCase() }}:</div>
                                    <div class="col"><b-form-input type="text" id="name-input" :placeholder="$t('forms.name') + ' (' + lang.toUpperCase() + ')'" v-model="page.name" :state="nameState"/></div>
                                </div>
                                <div class="row align-items-center mt-1">
                                    <div class="col-auto text-right min-45px-w">EN:</div>
                                    <div class="col"><b-form-input type="text" id="nameEn-input" :placeholder="$t('forms.name') + ' (EN)'" v-model="page.nameEn" :state="nameEnState"/></div>
                                </div>
                            </b-form-group>

                            <b-form-group :label="$t('forms.title')" label-for="title-input" class="mb-4">
                                <div class="row align-items-center">
                                    <div class="col-auto text-right min-45px-w">{{ lang.toUpperCase() }}:</div>
                                    <div class="col"><b-form-input type="text" id="title-input" :placeholder="$t('forms.title') + ' (' + lang.toUpperCase() + ')'" v-model="page.title" :state="titleState"/></div>
                                </div>
                                <div class="row align-items-center mt-1">
                                    <div class="col-auto text-right min-45px-w">EN:</div>
                                    <div class="col"><b-form-input type="text" id="titleEn-input" :placeholder="$t('forms.title') + ' (EN)'" v-model="page.titleEn" :state="titleEnState"/></div>
                                </div>
                            </b-form-group>

                            <b-form-group :label="$t('forms.description')" label-for="description-input" class="mb-4">
                                <div class="row align-items-center">
                                    <div class="col-auto text-right min-45px-w">{{ lang.toUpperCase() }}:</div>
                                    <div class="col"><b-form-input type="text" id="description-input" :placeholder="$t('forms.description') + ' (' + lang.toUpperCase() + ')'" v-model="page.description" :state="descriptionState"/></div>
                                </div>
                                <div class="row align-items-center mt-1">
                                    <div class="col-auto text-right min-45px-w">EN:</div>
                                    <div class="col"><b-form-input type="text" id="descriptionEn-input" :placeholder="$t('forms.description') + ' (EN)'" v-model="page.descriptionEn" :state="descriptionEnState"/></div>
                                </div>
                            </b-form-group>

                            <b-form-group :label="$t('forms.keywords')" label-for="keywords-input" class="mb-4">
                                <div class="row align-items-center">
                                    <div class="col-auto text-right min-45px-w">{{ lang.toUpperCase() }}:</div>
                                    <div class="col"><b-form-input type="text" id="keywords-input" :placeholder="$t('forms.keywords') + ' (' + lang.toUpperCase() + ')'" v-model="page.keywords" :state="keywordsState"/></div>
                                </div>
                                <div class="row align-items-center mt-1">
                                    <div class="col-auto text-right min-45px-w">EN:</div>
                                    <div class="col"><b-form-input type="text" id="keywordsEn-input" :placeholder="$t('forms.keywords') + ' (EN)'" v-model="page.keywordsEn" :state="keywordsEnState"/></div>
                                </div>
                            </b-form-group>


                            <div class="row">
                                <div class="col-lg-4">
                                    <b-form-group label="URL" label-for="url-input">
                                        <b-form-input type="text" id="url-input" placeholder="URL" v-model="page.url" :state="urlState"/>
                                    </b-form-group>
                                </div>
                                <div class="col-lg-8">
                                    <b-form-group :label="$t('forms.mainImage')" label-for="image-input">
                                        <b-button id="image-input" :variant="page.image && page.image.id ? 'outline-success' : 'outline-danger'" @click="openImagePicker('image-input')">
                                            {{page.image && page.image.id ? page.image.filename.substr(8) : $t('forms.select')}}
                                        </b-button>
                                        <b-popover v-if="page.image && page.image.id" target="image-input" triggers="hover" placement="right">
                                            <p class="m-0 p-0"><img :src="page.image.filename" class="browser-image"></p>
                                            <p class="m-0 p-0 text-center">{{ $t('forms.imageSize') }}: {{page.image.width}} x
                                                {{page.image.height}}</p>
                                        </b-popover>
                                    </b-form-group>
                                </div>
                            </div>

                            <b-form-group :label="$t('forms.content') + ' (' + lang.toUpperCase() + ')'" label-for="tinymce-input">
                                <editor ref="formEditor" id="tinymce-input" :api-key="editorApiKey" :init="editorConf" v-model="page.content"/>
                            </b-form-group>

                            <b-form-group :label="$t('forms.content') + ' (EN)'" label-for="tinymceEn-input">
                                <editor ref="formEditorEn" id="tinymceEn-input" :api-key="editorApiKey" :init="editorConfEn" v-model="page.contentEn"/>
                            </b-form-group>

                            <b-button variant="success" class="mr-2" @click="saveItem" :disabled="validated">{{ $t('forms.save') }}</b-button>
                            <b-button variant="light" v-on:click="cancel">{{ $t('forms.cancel') }}</b-button>
                            <b-button v-if="$attrs.mode === 'edit'" variant="danger" class="float-right" v-b-modal.confirm-delete>{{ $t('forms.delete') }}</b-button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <b-modal id="confirm-delete" :title="$t('forms.warning')" size="md" header-bg-variant="danger" header-text-variant="light" :cancelTitle="$t('forms.cancel')" :okTitle="$t('forms.delete')" @ok="deleteItem">
            <p class="text-center">{{ $t('forms.deleteConfirmationMessage') }}</p>
        </b-modal>

        <b-modal id="image-picker" :title="$t('forms.imagePickerTitle')" size="xl" header-bg-variant="primary" header-text-variant="light" :okTitle="$t('forms.close')" ok-only>
            <div class="row images">
                <div class="col-lg-1 my-auto text-center p-1" v-for="image in images" :key="image.id" :id="'image-wrapper-' + image.id">
                    <img :id="image.id" :src="image.filename" :alt="image.alt" class="browser-image" @click="pasteImage(image.id)">
                    <b-popover :target="'image-wrapper-' + image.id" triggers="hover" placement="top">
                        <div class="row">
                            <div class="col text-center">
                                <span><em>{{image.filename.substr(8)}}</em></span><br>
                                <span class="p-0">{{image.width + ' x ' + image.height}}</span>
                            </div>
                        </div>
                    </b-popover>
                </div>
            </div>
        </b-modal>
    </section>
</template>

<script>
    import axios from 'axios'
    import {Const} from '../../../main'
    var Editor = require('@tinymce/tinymce-vue').default;
    var app = null

    export default {
        name: 'Page',
        components: {
            'editor': Editor
        },
        computed: {
            nameState() {
                if (this.page.name) {
                    return this.page.name.length > 0
                } else {
                    return false
                }
            },
            nameEnState() {
                if (this.page.nameEn) {
                    return this.page.nameEn.length > 0
                } else {
                    return false
                }
            },
            titleState() {
                if (this.page.title) {
                    return this.page.title.length > 0
                } else {
                    return false
                }
            },
            titleEnState() {
                if (this.page.titleEn) {
                    return this.page.titleEn.length > 0
                } else {
                    return false
                }
            },
            descriptionState() {
                if (this.page.description) {
                    return this.page.description.length > 0
                } else {
                    return false
                }
            },
            descriptionEnState() {
                if (this.page.descriptionEn) {
                    return this.page.descriptionEn.length > 0
                } else {
                    return false
                }
            },
            keywordsState() {
                if (this.page.keywords) {
                    return this.page.keywords.length > 0
                } else {
                    return false
                }
            },
            keywordsEnState() {
                if (this.page.keywordsEn) {
                    return this.page.keywordsEn.length > 0
                } else {
                    return false
                }
            },
            urlState() {
                if (this.page.url) {
                    return this.page.url.length > 0 &&
                        this.page.url.startsWith('/') &&
                        this.page.url.split('/').length === 2
                } else {
                    return false
                }
            },
            imageState() {
                return this.page.image && this.page.image.id > 0
            },
            validated() {
                return !this.nameState || !this.nameEnState || !this.titleState || !this.titleEnState ||
                    !this.descriptionState || !this.descriptionEnState || !this.urlState ||
                    !this.keywordsState || !this.keywordsEnState || !this.imageState
            }
        },
        data() {
            return {
                lang: Const.LANG,
                images: [],
                page: {},
                pageName: '',
                editorApiKey: "jdsjwlv71ngv4ngm4fca30grz3lu6w833qma1br216ewfsqh",
                editorConf: {
                    language: Const.LANG,
                    min_height: 500,
                    menubar: false,
                    convert_urls: false,
                    toolbar_sticky: true,
                    plugins: [
                        'lists link image paste wordcount code autoresize'
                    ],
                    toolbar: 'undo redo | formatselect | bold italic | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link customImage | code',
                    block_formats: 'Paragraph=p; Heading 1=h1; Heading 2=h2; Heading 3=h3; Heading 4=h4; Heading 5=h5; Heading 6=h6',
                    contextmenu: "link image imagetools table",
                    autoresize_bottom_margin: 100,
                    placeholder: this.$t('forms.writeHere'),
                    setup: function (editor) {
                        editor.ui.registry.addButton('customImage', {
                            icon: 'image',
                            onAction: () => app.openImagePicker('tinymce-input')
                        });
                    }
                },
                editorConfEn: {
                    language: 'en',
                    min_height: 500,
                    menubar: false,
                    convert_urls: false,
                    toolbar_sticky: true,
                    plugins: [
                        'lists link image paste wordcount code autoresize'
                    ],
                    toolbar: 'undo redo | formatselect | bold italic | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link customImage | code',
                    block_formats: 'Paragraph=p; Heading 1=h1; Heading 2=h2; Heading 3=h3; Heading 4=h4; Heading 5=h5; Heading 6=h6',
                    contextmenu: "link image imagetools table",
                    autoresize_bottom_margin: 100,
                    placeholder: this.$t('forms.writeHere'),
                    setup: function (editor) {
                        editor.ui.registry.addButton('customImage', {
                            icon: 'image',
                            onAction: () => app.openImagePicker('tinymceEn-input')
                        });
                    }
                },
                imagePickerContext: ''
            }
        },
        mounted() {
            app = this
            if (this.$attrs.mode === Const.MODE_EDIT) {
                axios.get(Const.API_HOST + '/admin/page/get', {
                    params: {
                        'id': this.$route.params.id
                    }
                }).then(function (response) {
                    return response.data
                }).catch(function (error) {
                    // eslint-disable-next-line no-console
                    console.log(error)
                }).then(body => {
                    this.page = body
                    this.pageName = JSON.parse(JSON.stringify(this.page.name))
                })
            } else {
                this.page = {
                    name: '',
                    nameEn: '',
                    title: '',
                    titleEn: '',
                    description: '',
                    descriptionEn: '',
                    keywords: '',
                    keywordsEn: '',
                    image: {},
                    url: '',
                    content: '',
                    contentEn: ''
                }
            }
        },
        methods: {
            cancel() {
                this.$router.push('/pages')
            },
            saveItem() {
                var app = this
                axios.post(Const.API_HOST + '/admin/page/save', {
                    page: {
                        id: this.page.id,
                        name: this.page.name,
                        nameEn: this.page.nameEn,
                        title: this.page.title,
                        titleEn: this.page.titleEn,
                        description: this.page.description,
                        descriptionEn: this.page.descriptionEn,
                        keywords: this.page.keywords,
                        keywordsEn: this.page.keywordsEn,
                        image: this.page.image,
                        content: this.page.content,
                        contentEn: this.page.contentEn,
                        url: this.page.url
                    }
                }).then(function (response) {
                    // eslint-disable-next-line no-console
                    console.log(response)
                    if (response.status === 200) {
                        app.$router.push('/pages')
                    }
                }).catch(function (error) {
                    // eslint-disable-next-line no-console
                    console.log(error)
                })
            },
            deleteItem() {
                var app = this
                axios.delete(Const.API_HOST + '/admin/page/delete', {
                    data: {
                        id: this.page.id
                    }
                }).then(function (response) {
                    // eslint-disable-next-line no-console
                    console.log(response)
                    if (response.status === 200) {
                        app.$router.push('/pages')
                    }
                }).catch(function (error) {
                    // eslint-disable-next-line no-console
                    console.log(error)
                })
            },
            openImagePicker(context) {
                this.imagePickerContext = context
                this.$bvModal.show('image-picker')
                axios.get(Const.API_HOST + '/admin/images')
                    .then(function (response) {
                        return response.data
                    }).catch(function (error) {
                    // eslint-disable-next-line no-console
                    console.log(error)
                }).then(body => {
                    this.images = body
                })
            },
            pasteImage(id) {
                var selected = this.images.find(obj => {
                    return obj.id === id
                })

                // eslint-disable-next-line no-console
                console.log(selected.filename)
                var html = '<p class="par-with-images"><img src="' + selected.filename + '" alt="' + selected.alt + '" width="' + selected.width + 'px" height="' + selected.height + 'px"></p>'

                if (this.imagePickerContext === 'tinymce-input') {
                    this.$refs.formEditor.editor.execCommand('mceInsertContent', false, html);
                } else if (this.imagePickerContext === 'tinymceEn-input') {
                    this.$refs.formEditorEn.editor.execCommand('mceInsertContent', false, html);
                } else if (this.imagePickerContext === 'image-input') {
                    this.page.image = JSON.parse(JSON.stringify(selected))
                }

                this.$bvModal.hide('image-picker')
            }
        }
    }
</script>

<style scoped>
    .clickable /deep/ tbody td {
        cursor: pointer;
    }

    .browser-image {
        max-width: 100%;
        max-height: 100%;
        cursor: pointer;
    }

    /deep/ .tox-editor-container div:first-child {
        top: 58px !important;
    }
</style>
